import { connect } from 'react-redux';
import React from 'react';
import ReactDOM from 'react-dom';
import { PanelGroup } from 'react-bootstrap';
import { Common } from 'wsm-common-data';
import { ErrorBoundary } from 'wsm-error-boundary';
import { setNewRelicCustomAttribute } from '@ddc/new-relic';
import logger from 'ws-scripts/modules/logger';
import { waitForInvData } from 'wsm-srp-utilities';
import { isBrowser } from 'ws-scripts/modules/environment';
import Modals from '../components/FacetModals';
import { addAllFacetItem } from '../utilities/add-all-facet-item';
import FacetGroup from '../components/FacetGroup';
import MultiFacetGroup from '../components/MultiFacetGroup';
import NestedFacetNavigation from '../components/nested/NestedFacetNavigation';
import {
	fuelEfficiencyFacetIds,
	detailFacetLabels,
	detailFacetLabelsObject
} from '../global-constants';
import { arrayInsertSingle } from '../utilities/array-insert-single';
import '../components/style.scss';

class WidgetContainer extends React.PureComponent {
	constructor(props) {
		super(props);
		this.state = {
			activeFacetModal: null,
			renderPortal: false,
			isOpenModal: false,
			isMutilFacetGroups: false,
			modalTitle: '',
			modalContent: {}
		};

		if (isBrowser) {
			waitForInvData(this, 'facet');
		}
		this.closeModal = this.closeModal.bind(this);
		this.openModal = this.openModal.bind(this);
	}

	componentDidMount() {
		if (isBrowser && window.DDC?.pubsub) {
			// Subscribe to ws-inv-filters modal display events
			window.DDC.pubsub.subscribe('ws-inv-filters/modal-show', () => {
				this.setState({ renderPortal: true });
			});
			window.DDC.pubsub.subscribe('ws-inv-filters/modal-hide', () => {
				this.setState({ renderPortal: false });
				if (typeof window.scrollTo !== 'undefined') {
					window.scrollTo(0, 0);
				}
			});
		}
	}

	// eslint-disable-next-line react/sort-comp
	openModal = (
		facetId,
		modalTitleMarkup = '',
		modalContentMarkup = {},
		isMutilFacetGroupsMarkup = false
	) => {
		this.setState({
			activeFacetModal: facetId,
			isOpenModal: true,
			modalTitle: isMutilFacetGroupsMarkup
				? modalTitleMarkup
				: modalContentMarkup.title,
			modalContent: modalContentMarkup,
			isMutilFacetGroups: isMutilFacetGroupsMarkup
		});
	};

	// eslint-disable-next-line react/sort-comp
	static formatFacetLabel(title) {
		return title.toLocaleUpperCase();
	}

	static mappingLabel(labels, facetId) {
		if (!detailFacetLabelsObject[facetId]) {
			return {};
		}
		const {
			detailLabel,
			subTextLabel,
			aboutLinkLabel,
			titleLabel,
			learnMoreLinkLabel
		} = detailFacetLabelsObject[facetId];
		return {
			details: labels.get(detailLabel),
			subText: labels.get(subTextLabel),
			aboutLink: `${labels.get('ABOUT')} ${labels.get(aboutLinkLabel)}`,
			title: labels.get(titleLabel),
			learnMoreLink: labels.get(learnMoreLinkLabel),
			facetId
		};
	}

	static getMultiFacetModalContent(labels, groupTitleLabel, multiFacets) {
		const evFacetSubContents = multiFacets
			.map((subFacet) => {
				const facetLabel = WidgetContainer.formatFacetLabel(
					subFacet?.facet?.id
				);
				return {
					title: labels.get(`EV_${facetLabel}_TITLE_VERBIAGE`),
					details: labels.get(`EV_${facetLabel}_DETAILS_VERBIAGE`)
				};
			})
			.filter((item) => item.details);
		const facetLabelName =
			WidgetContainer.formatFacetLabel(groupTitleLabel);
		return {
			mainDetail: labels.get(`EV_${facetLabelName}_DETAILS_VERBIAGE`),
			subContent: evFacetSubContents,
			subText: labels.get(`EV_${facetLabelName}_SUB_TEXT_VERBIAGE`)
		};
	}

	/**
	 * Get modal/detail-link content for a single facet entry.
	 * Centralizes the evInfoPageHref, normalFuelType, and classification
	 * special-case logic used by both accordion and nested modes.
	 *
	 * @param {Object} entry - A facetEntry from buildFacetGroupData
	 * @param {Object} labels - Labels map from Common
	 * @param {Object} prefs - Prefs object from Common
	 * @param {Object} sitemap - Sitemap map from Common
	 * @returns {Object|null} modalContent for the facet, or null
	 */
	static getSingleFacetModalContent(entry, labels, prefs, sitemap) {
		let modalContent = entry.isDetailLink
			? WidgetContainer.mappingLabel(labels, entry.facet.id)
			: null;

		// Resolve evInfoPageHref for normalFuelType
		const evInfoPageHrefAlias =
			entry.facet.id === 'normalFuelType'
				? prefs.linkToEvInfoPageAlias
				: null;
		const evInfoPageHref = sitemap.get(evInfoPageHrefAlias);

		if (evInfoPageHref) {
			modalContent = { ...modalContent, evInfoPageHref };
		}
		// If href is not available hide learn more link
		if (!evInfoPageHref && modalContent) {
			modalContent.learnMoreLink = null;
		}

		// Classification "What's Different?" link
		if (
			entry.facet.id === 'classification' &&
			prefs.showClassificationLink
		) {
			modalContent = {
				title: entry.facet.label,
				// Atm, we need to pass a value to 'details' to ensure the CommonModal,
				// which wraps the CustomMarkupModal, renders correctly.
				details: '<></>',
				facetLabel: entry.facet.id,
				aboutLink: labels.get('WHATS_DIFFERENT'),
				learnMoreLink: null
			};
		}

		// Override details for normalFuelType
		if (entry.facet.id === 'normalFuelType' && modalContent) {
			modalContent.details = labels.get(prefs?.fuelTypeEvLabelAlias);
		}

		return modalContent;
	}

	closeModal = () => {
		this.setState({
			isOpenModal: false,
			activeFacetModal: null
		});
	};

	getModalOptions = () => {
		const { isOpenModal, modalTitle, modalContent, isMutilFacetGroups } =
			this.state;
		return {
			isOpen: isOpenModal,
			onClose: this.closeModal,
			onOpen: this.openModal,
			title: modalTitle,
			children: modalContent,
			isMutilFacetGroups
		};
	};

	/**
	 * Build normalized facet data shared by both accordion and nested modes.
	 * Iterates facets once and produces data objects consumed by each mode's
	 * renderer.
	 *
	 * @param {Object} data InvData.getFacets() response
	 * @returns {{ facetEntries, multiGroupEntry, multiGroupInsertIndex }}
	 */
	static buildFacetGroupData(data) {
		const appliedFilters = window.DDC.InvData.getAppliedFilters();

		/* TODO:
			Multi-facet groups should be configurable and the data
			should store this information.

			Each group should have:
			- facets contained in group
			- type of display per facet (select, radio, checkbox, etc.)
		*/

		const multiFacetGroupIds = fuelEfficiencyFacetIds;

		const isGeoLocationEnabled =
			isBrowser && !!window.DDC?.InvData?.getGeoData?.();

		const facetEntries = [];
		let multiGroupInsertIndex = 0;
		let multiGroupPanelIsCollapsed = false;

		data.facets.forEach((facet, idx) => {
			// Create "All" facet selection for single select facets
			// and set selected to true if no other facet values are selected
			// TODO THIS SHOULD BE ADDED TO THE FACET FROM WIS.
			if (!facet.multiselect) {
				// eslint-disable-next-line no-param-reassign
				facet.values = addAllFacetItem(facet.values, facet.allLabel);
			}

			const isMultiGroupFacet = multiFacetGroupIds.includes(facet.id);

			if (isMultiGroupFacet) {
				// Track insert position of first multi-group facet
				if (idx && !multiGroupInsertIndex) {
					multiGroupInsertIndex = idx;
				}
				// Set entire panel as collapsed if ANY of these facets are set to collapsed
				if (!multiGroupPanelIsCollapsed && facet.collapsed) {
					multiGroupPanelIsCollapsed = true;
				}
				return;
			}

			const facetSelections = appliedFilters.find(
				(filter) => filter.id === facet.id
			);
			const selectedValues = facetSelections
				? facetSelections.values
				: [];

			const isGeoFacet = facet.type === 'GEO';
			const isAccountId = facet.id === 'accountId';

			// accountId is only top-level when it immediately follows a GEO
			// facet in the list (adjacent). If it appears elsewhere, it renders
			// as a normal navigable facet in nested mode.
			const previousEntry = facetEntries[facetEntries.length - 1];
			const isAdjacentToGeo =
				previousEntry && previousEntry.facet.type === 'GEO';

			facetEntries.push({
				facet,
				selectedValues,
				isDetailLink:
					detailFacetLabels.includes(facet.id) &&
					!fuelEfficiencyFacetIds.includes(facet.id),
				// isTopLevel is only used by nested display mode (ignored by accordion).
				isTopLevel:
					isGeoFacet ||
					(isGeoLocationEnabled && isAccountId && isAdjacentToGeo)
			});
		});

		// Build multi-facet group data.
		// Only fuel efficiency facets are supported currently
		// for multi-facet group.
		const multiGroupFacets = data.facets.filter((facet) =>
			multiFacetGroupIds.includes(facet.id)
		);

		const multiFacets = multiGroupFacets.map((mf) => {
			const fs = appliedFilters.find((filter) => filter.id === mf.id);
			return { facet: mf, facetSelections: fs };
		});

		const multiGroupEntry = multiGroupFacets.length
			? {
					facets: multiFacets,
					groupId: 'fuelEfficiency-group',
					groupTitleLabel: 'MPG_MPGE',
					collapsed: multiGroupPanelIsCollapsed
			  }
			: null;

		return {
			facetEntries,
			multiGroupEntry,
			multiGroupInsertIndex
		};
	}

	/**
	 * Create a FacetGroup component for each object in data.facets
	 * (accordion display mode).
	 *
	 * @param {Object} data InvData.getFacets() response
	 * @param {Function} onOpen Modal open handler
	 * @returns {FacetGroup[]} FacetGroup components
	 */
	static getFacetGroups(data, onOpen) {
		const { facetEntries, multiGroupEntry, multiGroupInsertIndex } =
			WidgetContainer.buildFacetGroupData(data);

		const singlesGroup = facetEntries.map((entry) => (
			<Common
				key={`facetgroup-${entry.facet.id}`}
				render={({ labels, prefs, sitemap }) => {
					const modalContent =
						WidgetContainer.getSingleFacetModalContent(
							entry,
							labels,
							prefs,
							sitemap
						);
					return (
						<FacetGroup
							facet={entry.facet}
							openDetailsModal={onOpen}
							detailsLinkData={modalContent}
							selectedValues={entry.selectedValues}
						/>
					);
				}}
			/>
		));

		const multiGroup = multiGroupEntry ? (
			<Common
				key={`facetgroup-${multiGroupEntry.facets
					.map(({ facet }) => facet.id)
					.join('-')}`}
				render={({ labels }) => {
					const modalContent =
						WidgetContainer.getMultiFacetModalContent(
							labels,
							multiGroupEntry.groupTitleLabel,
							multiGroupEntry.facets
						);
					return (
						<MultiFacetGroup
							facets={multiGroupEntry.facets}
							groupId={multiGroupEntry.groupId}
							groupTitleLabel={multiGroupEntry.groupTitleLabel}
							openDetailsModal={onOpen}
							detailsLinkData={modalContent}
							collapsed={multiGroupEntry.collapsed}
						/>
					);
				}}
			/>
		) : null;
		return arrayInsertSingle(
			singlesGroup,
			multiGroupInsertIndex,
			multiGroup
		);
	}

	static getFacetList(facets, options, activeFacetModal) {
		return (
			<ErrorBoundary
				errorHandler={(error, errorInfo) => {
					setNewRelicCustomAttribute(
						'SRP ERROR',
						`ws-inv-facets error boundary.\n${error}`
					);
					const newError = new Error(
						`ws-inv-facets error boundary.\n${error}`
					);
					newError.originalError = error;
					newError.originalStackTrace = errorInfo.componentStack;
					logger.error(`${newError}\n${newError.originalStackTrace}`);
				}}
			>
				<Common
					render={({ prefs, labels, sitemap }) => {
						const facetDisplay = prefs.facetDisplay || 'accordion';

						if (facetDisplay === 'nested') {
							const commonData = {
								labels,
								prefs,
								sitemap
							};
							return (
								<>
									<Modals
										activeFacetModal={activeFacetModal}
										options={options}
									/>
									{WidgetContainer.renderNestedMode(
										facets,
										options.onOpen,
										commonData
									)}
								</>
							);
						}

						return (
							<PanelGroup id="facet-list" className="facet-list">
								<Modals
									activeFacetModal={activeFacetModal}
									options={options}
								/>
								{WidgetContainer.getFacetGroups(
									facets,
									options.onOpen
								)}
							</PanelGroup>
						);
					}}
				/>
			</ErrorBoundary>
		);
	}

	/**
	 * Build nested mode facet data and render NestedFacetNavigation.
	 * Top-level facets (GEO, adjacent accountId) are rendered inline,
	 * while navigable facets are rendered as title rows with detail
	 * panels. All are kept in a single ordered array — each entry
	 * carries an isTopLevel flag so the nested list component can
	 * decide how to render it.
	 *
	 * Uses the shared buildFacetGroupData() helper so that facet
	 * iteration, multi-group assembly, and modal content logic are
	 * not duplicated between accordion and nested modes.
	 *
	 * @param {Object} data InvData.getFacets() response
	 * @param {Function} onOpen Modal open handler
	 * @param {Object} commonData Destructured Common context { labels, prefs, sitemap }
	 */
	static renderNestedMode(data, onOpen, commonData) {
		const { labels, prefs, sitemap } = commonData;
		const { facetEntries, multiGroupEntry, multiGroupInsertIndex } =
			WidgetContainer.buildFacetGroupData(data);

		// Convert entries to the shape NestedFacetNavigation expects.
		// Top-level entries include pre-rendered FacetGroup content; navigable
		// entries use getSingleFacetModalContent for detail-link data (shared with
		// accordion mode).
		const facetGroups = facetEntries.map((entry) => {
			if (entry.isTopLevel) {
				return {
					facet: entry.facet,
					isTopLevel: true,
					renderedContent: (
						<FacetGroup
							facet={entry.facet}
							selectedValues={entry.selectedValues}
							renderPanel={false}
							isTopLevel
						/>
					)
				};
			}

			const modalContent = WidgetContainer.getSingleFacetModalContent(
				entry,
				labels,
				prefs,
				sitemap
			);

			return {
				facet: entry.facet,
				selectedValues: entry.selectedValues,
				detailsLinkData: modalContent,
				openDetailsModal: onOpen
			};
		});

		// Insert multi-facet group at the correct position (matching accordion).
		// Passes groupId and groupTitleLabel directly — the same props accordion
		// gives to MultiFacetGroup — so downstream components resolve the title
		// via useLabels().
		if (multiGroupEntry) {
			const multiGroupFacets = multiGroupEntry.facets;

			const modalContent = WidgetContainer.getMultiFacetModalContent(
				labels,
				multiGroupEntry.groupTitleLabel,
				multiGroupFacets
			);

			const multiGroupItem = {
				groupId: multiGroupEntry.groupId,
				groupTitleLabel: multiGroupEntry.groupTitleLabel,
				detailsLinkData: modalContent,
				openDetailsModal: onOpen,
				isMultiFacetGroup: true,
				multiFacets: multiGroupFacets
			};

			facetGroups.splice(multiGroupInsertIndex, 0, multiGroupItem);
		}

		return <NestedFacetNavigation facetGroups={facetGroups} />;
	}

	render() {
		const options = this.getModalOptions();
		const facets =
			(isBrowser &&
				window.DDC?.InvData?.srpReady &&
				window.DDC?.InvData?.getFacets &&
				window.DDC?.InvData?.getFacets()) ||
			undefined;
		if (!facets || Object.keys(window.DDC.InvData.facets).length === 0) {
			return null;
		}

		const [modalContainer] = document.querySelectorAll(
			'.modal-filters-react-portal'
		);

		const isRenderPortal = modalContainer && this.state.renderPortal;

		const facetList = WidgetContainer.getFacetList(
			facets,
			options,
			this.state.activeFacetModal
		);

		// Renders the `facetList` into a modal based on event type
		if (isRenderPortal) {
			return ReactDOM.createPortal(facetList, modalContainer);
		}

		return facetList;
	}
}

WidgetContainer.propTypes = {};

const mapStateToProps = (_state) => {
	const {
		requestData: { widgetName }
	} = _state;
	return { widgetName };
};

const mapDispatchToProps = (_dispatch) => ({});

const Widget = connect(mapStateToProps, mapDispatchToProps)(WidgetContainer);

export default Widget;
